import { ViewHook } from "phoenix_live_view";

import type * as LightweightCharts from "lightweight-charts";
import type {
  AreaData,
  IChartApi,
  IPriceLine,
  ISeriesApi,
  MouseEventParams,
  SeriesMarker,
  Time,
} from "lightweight-charts";

import { fetchSignalChartData, type SignalChartData } from "../services/api";
import { newThemeChangeListener, type ThemeChangeListener } from "../utils/app-theme";
import { whenVisible, type VisibilityWait } from "../utils/charts";
import { pageLocale } from "../utils/formatters";
import {
  chartTextColor,
  priceChartCrosshairLineColor,
  priceChartDownsideLineBottomColor,
  priceChartDownsideLineColor,
  priceChartDownsideLineTopColor,
  priceChartGridLineColor,
  priceChartPrevCloseLabelColor,
  priceChartPrevCloseLineColor,
  priceChartPrevCloseTextColor,
  priceChartUpsideLineBottomColor,
  priceChartUpsideLineColor,
  priceChartUpsideLineTopColor,
  signalChartBeforeEntryLineBottomColor,
  signalChartBeforeEntryLineColor,
  signalChartBeforeEntryLineTopColor,
  signalChartEntryMarkerColor,
} from "../utils/colors";

type ChartLibrary = typeof LightweightCharts;

type Tone = "upside" | "downside";

interface SignalPriceChartLabels {
  fund_entry: string;
  opened_at: string;
  reached: string;
}

interface AutoscaleInfo {
  priceRange?: {
    minValue?: number;
    maxValue?: number;
  };
}

const tooltipDistanceFromPointer = 12;

const priceFormatter = new Intl.NumberFormat(pageLocale(), {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const smallPriceFormatter = new Intl.NumberFormat(pageLocale(), {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const dateFormatter = new Intl.DateTimeFormat(pageLocale() === "en-US" ? "en-GB" : pageLocale(), {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const axisPriceFormatter = new Intl.NumberFormat(pageLocale(), {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatPrice = (price: number) =>
  (price < 1 ? smallPriceFormatter : priceFormatter).format(price);

const toneColors = (tone: Tone) =>
  tone === "upside"
    ? {
        lineColor: priceChartUpsideLineColor,
        topColor: priceChartUpsideLineTopColor,
        bottomColor: priceChartUpsideLineBottomColor,
      }
    : {
        lineColor: priceChartDownsideLineColor,
        topColor: priceChartDownsideLineTopColor,
        bottomColor: priceChartDownsideLineBottomColor,
      };

const windowTone = ({ bars }: SignalChartData): Tone =>
  bars.at(-1)!.value >= bars[0].value ? "upside" : "downside";

class SignalPriceChart extends ViewHook {
  declare container: HTMLElement;
  declare tooltip: HTMLElement;
  declare tooltipPrice: HTMLElement;
  declare tooltipLabel: HTMLElement;
  declare tooltipDate: HTMLElement;
  declare pulse: HTMLElement | null;

  private chart?: IChartApi;
  private series?: ISeriesApi<"Area">;
  private fundPriceLine?: IPriceLine;
  private data?: SignalChartData;
  private labels!: SignalPriceChartLabels;
  private markerLabels!: Map<string, string>;

  private visibilityWait?: VisibilityWait;
  private themeChangeListener?: ThemeChangeListener;
  private isDestroyed?: boolean;

  // LiveView ignores mounted()'s return value, so the promise is safe to return.
  // oxlint-disable-next-line typescript/no-misused-promises
  async mounted() {
    this.markerLabels = new Map();
    this.labels = JSON.parse(this.el.dataset.labels!) as SignalPriceChartLabels;
    this.handleCrosshairMove = this.handleCrosshairMove.bind(this);
    this.handleSizeChange = this.handleSizeChange.bind(this);
    this.positionPulse = this.positionPulse.bind(this);

    this.container = this.el.querySelector<HTMLElement>('[data-part="chart"]')!;
    this.tooltip = this.el.querySelector<HTMLElement>('[data-part="tooltip"]')!;
    this.tooltipPrice = this.el.querySelector<HTMLElement>('[data-part="tooltip-price"]')!;
    this.tooltipLabel = this.el.querySelector<HTMLElement>('[data-part="tooltip-label"]')!;
    this.tooltipDate = this.el.querySelector<HTMLElement>('[data-part="tooltip-date"]')!;
    this.pulse = this.el.querySelector<HTMLElement>('[data-part="pulse"]');

    this.visibilityWait = whenVisible(this.el, 0);
    await this.visibilityWait.visible;
    this.visibilityWait = undefined;

    const [data, library] = await Promise.all([
      fetchSignalChartData(this.el.dataset.signalId!),
      import("lightweight-charts"),
    ]);

    if (this.isDestroyed || data.bars.length === 0) {
      return;
    }

    this.data = data;
    this.renderChart(library, data);
  }

  destroyed() {
    this.isDestroyed = true;

    this.visibilityWait?.cancel();
    this.themeChangeListener?.detach();

    this.chart?.remove();
    this.chart = undefined;
  }

  private renderChart(library: ChartLibrary, data: SignalChartData) {
    const { AreaSeries, LineStyle, createChart, createSeriesMarkers } = library;
    const pastMode = this.el.dataset.mode === "past";
    const tone = pastMode ? (this.el.dataset.tone as Tone) : windowTone(data);

    this.chart = createChart(this.container, {
      autoSize: true,
      localization: { locale: pageLocale(), priceFormatter: axisPriceFormatter.format },
      layout: {
        background: { color: "transparent" },
        textColor: chartTextColor(),
        fontSize: 10,
        attributionLogo: false,
      },
      crosshair: {
        horzLine: { visible: false, labelVisible: false },
        vertLine: { labelVisible: false, color: priceChartCrosshairLineColor() },
      },
      grid: {
        horzLines: { color: priceChartGridLineColor() },
        vertLines: { visible: false },
      },
      timeScale: { fixLeftEdge: true, rightOffsetPixels: 24, borderVisible: false },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.15, bottom: 0.1 } },
      handleScroll: false,
      handleScale: false,
    });

    this.series = this.chart.addSeries(AreaSeries, {
      ...toneColors(tone),
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      autoscaleInfoProvider: (original: () => AutoscaleInfo | null) =>
        this.includeFundPrice(original()),
    });

    this.series.setData(this.seriesData(data, pastMode));

    this.fundPriceLine = this.series.createPriceLine({
      price: data.fund_price,
      title: this.labels.fund_entry,
      color: priceChartPrevCloseLineColor(),
      axisLabelColor: priceChartPrevCloseLabelColor(),
      axisLabelTextColor: priceChartPrevCloseTextColor,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
    });

    createSeriesMarkers(this.series, this.markers(data));

    this.chart.timeScale().fitContent();
    this.chart.timeScale().subscribeSizeChange(this.handleSizeChange);
    this.chart.timeScale().subscribeVisibleLogicalRangeChange(this.positionPulse);
    this.chart.subscribeCrosshairMove(this.handleCrosshairMove);

    this.themeChangeListener = newThemeChangeListener(() => {
      this.applyThemeColors();
    });
  }

  private includeFundPrice(result: AutoscaleInfo | null) {
    const fundPrice = this.data!.fund_price;
    const range = result?.priceRange;

    if (range?.minValue === undefined || range.maxValue === undefined) {
      return result;
    }

    range.minValue = Math.min(range.minValue, fundPrice);
    range.maxValue = Math.max(range.maxValue, fundPrice);

    return result;
  }

  private seriesData(data: SignalChartData, pastMode: boolean): AreaData[] {
    return data.bars.map(({ time, value }) =>
      pastMode && time < data.entry.time
        ? {
            time,
            value,
            lineColor: signalChartBeforeEntryLineColor,
            topColor: signalChartBeforeEntryLineTopColor,
            bottomColor: signalChartBeforeEntryLineBottomColor,
          }
        : { time, value },
    );
  }

  private markers(data: SignalChartData): SeriesMarker<Time>[] {
    this.addMarkerLabel(
      data.entry.time,
      this.labels.opened_at.replace("%{price}", formatPrice(data.entry.price)),
    );

    data.hits.forEach(({ time, threshold }) => {
      this.addMarkerLabel(time, this.labels.reached.replace("%{threshold}", `+${threshold}%`));
    });

    const entryMarker: SeriesMarker<Time> = {
      time: data.entry.time,
      position: "inBar",
      shape: "circle",
      color: signalChartEntryMarkerColor,
      size: 1.2,
    };

    const hitMarkers = data.hits.map<SeriesMarker<Time>>(({ time }) => ({
      time,
      position: "inBar",
      shape: "circle",
      color: priceChartUpsideLineColor,
      size: 0.6,
    }));

    return [entryMarker, ...hitMarkers];
  }

  private addMarkerLabel(time: string, label: string) {
    const existing = this.markerLabels.get(time);

    this.markerLabels.set(time, existing ? `${existing} · ${label}` : label);
  }

  private handleSizeChange() {
    this.chart?.timeScale().fitContent();
  }

  private positionPulse() {
    const { pulse, chart, series, data } = this;

    if (!pulse || !chart || !series || !data) {
      return;
    }

    const entryBar = data.bars.find(({ time }) => time === data.entry.time)!;
    const x = chart.timeScale().timeToCoordinate(entryBar.time);
    const y = series.priceToCoordinate(entryBar.value);

    if (x === null || y === null) {
      pulse.hidden = true;
      return;
    }

    pulse.style.left = `${x}px`;
    pulse.style.top = `${y}px`;
    pulse.hidden = false;
  }

  private handleCrosshairMove(param: MouseEventParams) {
    const { container, tooltip } = this;
    const { point } = param;
    const value = (param.seriesData.get(this.series!) as AreaData | undefined)?.value;

    if (
      point === undefined ||
      value === undefined ||
      point.x < 0 ||
      point.x > container.clientWidth ||
      point.y < 0 ||
      point.y > container.clientHeight
    ) {
      tooltip.hidden = true;
      return;
    }

    const time = param.time as string;
    const label = this.markerLabels.get(time);

    this.tooltipPrice.textContent = formatPrice(value);
    this.tooltipLabel.textContent = label ?? "";
    this.tooltipLabel.hidden = label === undefined;
    this.tooltipDate.textContent = dateFormatter.format(new Date(time));

    tooltip.hidden = false;

    const left = Math.min(
      Math.max(point.x - tooltip.clientWidth / 2, 0),
      container.clientWidth - tooltip.clientWidth,
    );

    const above = point.y - tooltip.clientHeight - tooltipDistanceFromPointer;
    const top = above < 0 ? point.y + tooltipDistanceFromPointer : above;

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }

  private applyThemeColors() {
    this.chart?.applyOptions({
      layout: { textColor: chartTextColor() },
      crosshair: { vertLine: { color: priceChartCrosshairLineColor() } },
      grid: { horzLines: { color: priceChartGridLineColor() } },
    });

    this.fundPriceLine?.applyOptions({
      color: priceChartPrevCloseLineColor(),
      axisLabelColor: priceChartPrevCloseLabelColor(),
    });
  }
}

export default SignalPriceChart;
